import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Collapse, Icon } from '@pella/sash-framework';
import { VideoObject } from '../structured-data';
import { initDataLayer } from '../../utilities/functions';
import { Image } from '../../';

import styles from './index.module.scss';

const Video = ({ data, className, title, urlParams, youtube = true, id, children, videoAttributes = {}, ...other }) => {
	const [show, setShow] = useState(true);
	const [isAutoPlay, setIsAutoPlay] = useState(data?.autoplay);
	const [isInternal, setIsInternal] = useState(false);

	useEffect(() => {
		if (!data?.image_overlay?.[0] || !youtube) setShow(false);
		if (data?.internal_video?.url || !youtube) setIsInternal(true);

		if (data?.image_overlay?.[0] && youtube) {
			setShow(true);
			if (!data.autoplay) setIsAutoPlay(true);
		}
	}, [data, youtube]);

	if (!data?.video?.video_id && !data?.internal_video?.url) return null;

	const { video = {}, image_overlay = [], transcript, internal_video = {}, schema = {} } = data;

	const showDuration = (duration) => {
		const splitDuration = duration.split('PT')[1].split('M');
		const minute = splitDuration[0];
		const seconds = splitDuration[1].split('S')[0];

		return (
			<p className={styles.videoDuration}>
				{minute}:{seconds}
			</p>
		);
	};

	const onVideoPosterClick = () => {
		setShow(!show);
		initDataLayer('videoplay');
	};

	return (
		<>
			<div
				className={className || ''}
				data-testid='video'
				{...other}
			>
				<div className={styles.videoWrapper}>
					{show && (
						<button
							className={styles.video_Thumbnail}
							onClick={onVideoPosterClick}
						>
							<span className={styles.srOnly}>Watch the Video</span>
							<div className={styles.video_PlayIcon}>
								<Icon
									name='play_arrow'
									className='!text-[40px] sm:!text-[60px]'
								/>
							</div>

							{image_overlay?.[0] && (
								<Image
									data={image_overlay[0]}
									className={styles.video_ThumbnailImage}
								/>
							)}
						</button>
					)}

					{show && children}

					{!isInternal && !show && (
						<iframe
							loading='lazy'
							className={styles.video_Embed}
							src={`https://www.youtube.com/embed/${video.video_id}?rel=0&enablejsapi=1&autoplay=${isAutoPlay === true ? 1 : 0}&mute=${isAutoPlay === true ? 1 : 0}${urlParams || ''}`}
							title={title || video.title}
							allowFullScreen
						/>
					)}

					{isInternal && !show && (internal_video?.url || video?.video_id) && (
						// eslint-disable-next-line jsx-a11y/media-has-caption -- fix this
						<video
							id={id}
							className={styles.video_Html5}
							autoPlay={!show}
							muted={isAutoPlay}
							loop={isAutoPlay}
							playsInline={isAutoPlay}
							preload={isAutoPlay ? 'auto' : 'none'}
							poster={image_overlay?.[0]?.image?.url}
							{...videoAttributes}
						>
							<source
								src={internal_video?.url || video.video_id}
								type='video/mp4'
							/>
						</video>
					)}

					{show && schema?.duration && showDuration(schema.duration)}
				</div>

				{!show && transcript && (
					<Collapse className={styles.video_Transcript}>
						<Collapse.Trigger>Transcript</Collapse.Trigger>
						<Collapse.Content>
							<div dangerouslySetInnerHTML={{ __html: transcript }} />
						</Collapse.Content>
					</Collapse>
				)}
			</div>

			{schema && (
				<VideoObject
					schema={schema}
					id={video.video_id}
				/>
			)}
		</>
	);
};

Video.propTypes = {
	data: PropTypes.object,
	title: PropTypes.string,
	autoPlay: PropTypes.bool,
	urlParams: PropTypes.string,
	youtube: PropTypes.bool,
	id: PropTypes.string,
	className: PropTypes.string,
	children: PropTypes.node,
	videoAttributes: PropTypes.object,
};

export default Video;
